import { KEYCLOAK_ROLE_SUPERADMIN } from 'core/constants/role'
import { IClient, IKeycloakUtilisateur } from 'core/interfaces/User'
import { UseKeycloakService } from 'core/packages/KeycloakProvider'
import React, { createContext, ReactNode, useCallback, useMemo } from 'react'

interface IUserContextType {
  clients: IClient[]
  setClients: (clients: IClient[]) => void
  isAdmin: boolean
  isSuperAdmin: boolean
  user: IKeycloakUtilisateur | null
  setCurrentUser: (user: IKeycloakUtilisateur | null) => void
  updateUser: (partialUser: Partial<IKeycloakUtilisateur>) => void
  getClientId: () => number | null
}

export const UserContext = createContext<IUserContextType>({
  clients: [],
  setClients: () => {},
  isAdmin: false,
  isSuperAdmin: false,
  user: null,
  setCurrentUser: () => {},
  updateUser: () => {},
  getClientId: () => null,
})

const saveIdClientToStorage = (idClient: number | undefined) => {
  if (!idClient) return
  localStorage.setItem('idClient', idClient.toString())
}

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const kc = UseKeycloakService()
  const isSuperAdmin = kc.checkRole(KEYCLOAK_ROLE_SUPERADMIN)

  const [clients, setClients] = React.useState<IClient[]>([])
  const [user, setUser] = React.useState<IKeycloakUtilisateur | null>(null)
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false)

  const setCurrentUser = useCallback((newUser: IKeycloakUtilisateur | null) => {
    if (!newUser) return
    setUser(newUser)
    saveIdClientToStorage(newUser.idClient)
    setIsAdmin((newUser?.tagsAdministrateur ?? []).length > 0)
  }, [])

  const updateUser = useCallback(
    (partialUser: Partial<IKeycloakUtilisateur>) => {
      setUser((prevUser) => {
        const updatedUser = prevUser
          ? { ...prevUser, ...partialUser }
          : (partialUser as IKeycloakUtilisateur)
        saveIdClientToStorage(updatedUser.idClient)
        return updatedUser
      })
    },
    []
  )

  const getClientId = useCallback(() => user?.idClient ?? null, [user])

  const value = useMemo(
    () => ({
      clients,
      setClients,
      isAdmin,
      isSuperAdmin,
      user,
      setCurrentUser,
      updateUser,
      getClientId,
    }),
    [
      clients,
      isAdmin,
      isSuperAdmin,
      user,
      setCurrentUser,
      updateUser,
      getClientId,
    ]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
